import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCRMAuth } from '@/contexts/CRMAuthContext';
import CRMLayout from './CRMLayout';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Building2,
  Globe,
  Mail,
  Phone,
  User,
  Calendar,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  Archive,
  Filter,
  Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function CRMClients() {
  const { token, isAdmin } = useCRMAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [showAddModal, setShowAddModal] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    client_name: '',
    company_name: '',
    website: '',
    industry: '',
    contact_person: '',
    phone: '',
    email: '',
    account_manager_id: '',
    monthly_retainer: '',
    notes: ''
  });

  useEffect(() => {
    fetchClients();
    fetchEmployees();
  }, [statusFilter]);

  const fetchClients = async () => {
    try {
      const res = await fetch(`${API}/api/crm/clients/?status=${statusFilter}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClients(data.clients);
      }
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await fetch(`${API}/api/crm/users?role=employee`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEmployees(data.users);
      }
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  };

  const handleSaveClient = async () => {
    if (!formData.client_name) {
      toast.error('Client name is required');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API}/api/crm/clients/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          monthly_retainer: parseFloat(formData.monthly_retainer) || 0
        })
      });

      if (res.ok) {
        toast.success('Client added successfully!');
        setShowAddModal(false);
        setFormData({
          client_name: '',
          company_name: '',
          website: '',
          industry: '',
          contact_person: '',
          phone: '',
          email: '',
          account_manager_id: '',
          monthly_retainer: '',
          notes: ''
        });
        fetchClients();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add client');
      }
    } catch (err) {
      toast.error('Failed to add client');
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveClient = async (clientId) => {
    if (!confirm('Are you sure you want to archive this client?')) return;

    try {
      const res = await fetch(`${API}/api/crm/clients/${clientId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Client archived');
        fetchClients();
      }
    } catch (err) {
      toast.error('Failed to archive client');
    }
  };

  const filteredClients = clients.filter(c => 
    c.client_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.company_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.contact_person?.toLowerCase().includes(search.toLowerCase())
  );

  const INDUSTRIES = [
    'Technology', 'E-commerce', 'Healthcare', 'Finance', 'Education',
    'Real Estate', 'Manufacturing', 'Retail', 'Food & Beverage', 'Other'
  ];

  return (
    <CRMLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Clients</h1>
          <p className="text-slate-500">Manage your agency clients</p>
        </div>
        {isAdmin() && (
          <Button onClick={() => setShowAddModal(true)} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-2" /> Add Client
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search clients..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Clients Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : filteredClients.length === 0 ? (
        <Card className="p-12 text-center">
          <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No clients found</h3>
          <p className="text-slate-500 mb-4">
            {search ? 'Try a different search term' : 'Add your first client to get started'}
          </p>
          {isAdmin() && !search && (
            <Button onClick={() => setShowAddModal(true)}>
              <Plus className="w-4 h-4 mr-2" /> Add Client
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClients.map(client => (
            <Card key={client.id} className="p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                    {client.client_name?.charAt(0) || 'C'}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{client.client_name}</h3>
                    {client.company_name && (
                      <p className="text-sm text-slate-500">{client.company_name}</p>
                    )}
                  </div>
                </div>
                <Badge variant={client.status === 'active' ? 'default' : 'secondary'}>
                  {client.status}
                </Badge>
              </div>

              <div className="space-y-2 mb-4">
                {client.industry && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Building2 className="w-4 h-4" />
                    <span>{client.industry}</span>
                  </div>
                )}
                {client.website && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Globe className="w-4 h-4" />
                    <a href={client.website} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline truncate">
                      {client.website.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                )}
                {client.contact_person && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <User className="w-4 h-4" />
                    <span>{client.contact_person}</span>
                  </div>
                )}
                {client.account_manager_name && (
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <User className="w-4 h-4" />
                    <span>Manager: {client.account_manager_name}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 pt-4 border-t border-slate-100">
                <Button variant="outline" size="sm" className="flex-1" asChild>
                  <Link to={`/crm/clients/${client.id}`}>
                    <Eye className="w-4 h-4 mr-1" /> View
                  </Link>
                </Button>
                {isAdmin() && (
                  <>
                    <Button variant="outline" size="sm" asChild>
                      <Link to={`/crm/clients/${client.id}/edit`}>
                        <Edit className="w-4 h-4" />
                      </Link>
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleArchiveClient(client.id)}
                    >
                      <Archive className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add Client Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Client</DialogTitle>
            <DialogDescription>
              Enter the client details below to add them to your agency.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2 sm:col-span-1">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Client Name *</label>
              <Input
                placeholder="e.g., Smart Infovision"
                value={formData.client_name}
                onChange={(e) => setFormData({...formData, client_name: e.target.value})}
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Company Name</label>
              <Input
                placeholder="e.g., Smart Infovision Pvt Ltd"
                value={formData.company_name}
                onChange={(e) => setFormData({...formData, company_name: e.target.value})}
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Website</label>
              <Input
                placeholder="https://example.com"
                value={formData.website}
                onChange={(e) => setFormData({...formData, website: e.target.value})}
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Industry</label>
              <Select value={formData.industry} onValueChange={(v) => setFormData({...formData, industry: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map(ind => (
                    <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Contact Person</label>
              <Input
                placeholder="Primary contact name"
                value={formData.contact_person}
                onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Phone</label>
              <Input
                placeholder="+91 98765 43210"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Email</label>
              <Input
                type="email"
                placeholder="client@example.com"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Account Manager</label>
              <Select value={formData.account_manager_id} onValueChange={(v) => setFormData({...formData, account_manager_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select manager" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Monthly Retainer (₹)</label>
              <Input
                type="number"
                placeholder="50000"
                value={formData.monthly_retainer}
                onChange={(e) => setFormData({...formData, monthly_retainer: e.target.value})}
              />
            </div>
            <div className="col-span-2">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Notes</label>
              <Textarea
                placeholder="Any additional notes about this client..."
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button onClick={handleSaveClient} disabled={saving}>
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Add Client'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CRMLayout>
  );
}
